/**
 * KAN-858: ручная переписка в карточке клиента приложения менеджера поверх
 * хуков кабинета (useManualChat, useReturnToBot, useClientConversationTarget):
 *   - кто ведёт: ручной режим включён -> менеджер, иначе бот;
 *   - "Взять переписку" включает ручной режим, "Вернуть боту" после
 *     подтверждения возвращает клиента боту (снимает и передачу менеджеру);
 *   - отправка с аккаунта диалога, как в кабинете (KAN-663): несколько наших
 *     аккаунтов -> выбор, по умолчанию последний; иначе account_id не уходит,
 *     сообщение пишется в последнюю смету с перепиской (KAN-641);
 *     KAN-871: свой личный Telegram - вариант "От себя", чужие личные скрыты;
 *   - ручной режим выключен: отправка сначала спрашивает "Бот замолчит, пока
 *     вы ведёте переписку", после согласия включает режим и отправляет;
 *     отказ оставляет текст в поле;
 *   - писать и переключать могут те, кто может менять клиента
 *     (clientManagerRights.canChange); остальным только чтение.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useManualChat } from '@/components/clients/useManualChat';
import { useReturnToBot } from '@/components/clients/useReturnToBot';
import { useClientConversationTarget } from '@/components/clients/useClientConversationTarget';
import { transcriptQueryKey } from '@/components/clients/useTranscriptMessages';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { useSenderAccountChoice } from '@/hooks/useSenderAccountChoice';
import { senderAccountText } from '@/lib/mobileClient';
import type { Client } from '@/types/client';
import type { EstimateMessage } from '@/types/estimate';

interface PendingSend {
  text: string;
  photo: File | null;
  resolve: (ok: boolean) => void;
}

export function useMobileManualChat(client: Client, messages: EstimateMessage[]) {
  const clientId = client.id;
  const { canChange: canWrite } = useClientManagerRights(client.manager?.id);
  const transcriptKey = useMemo(() => transcriptQueryKey(clientId, null), [clientId]);
  const { contactId } = useClientConversationTarget({ clientId, loadClient: false });

  // KAN-871: свой личный аккаунт "От себя", чужие личные не предлагаются.
  const {
    options: accountOptions,
    sendOption,
    sendChannel,
    chooseAccount,
    account,
  } = useSenderAccountChoice(messages, { resetKey: clientId, fallbackAccount: client.sender_account ?? null });

  const chat = useManualChat({
    clientId,
    initialManualMode: !!client.manual_mode,
    transcriptKey,
    enabled: true,
    estimateId: sendOption ? (sendOption.estimateId ?? contactId) : contactId,
    accountId: sendOption?.accountId ?? null,
    channel: sendChannel,
  });
  const returnToBot = useReturnToBot(clientId);

  const pending = useRef<PendingSend | null>(null);
  const [takeAsked, setTakeAsked] = useState(false);
  const [returnAsked, setReturnAsked] = useState(false);

  const { manualMode, send: chatSend, setManualModeAsync } = chat;
  const send = useCallback(
    (text: string, photo: File | null = null): Promise<boolean> => {
      if (!canWrite) return Promise.resolve(false);
      if (manualMode) return chatSend(text, photo);
      pending.current?.resolve(false);
      return new Promise<boolean>((resolve) => {
        pending.current = { text, photo, resolve };
        setTakeAsked(true);
      });
    },
    [canWrite, manualMode, chatSend]
  );

  const confirmTake = useCallback(async () => {
    const job = pending.current;
    pending.current = null;
    setTakeAsked(false);
    if (!job) return;
    const enabled = await setManualModeAsync(true);
    job.resolve(enabled ? await chatSend(job.text, job.photo) : false);
  }, [setManualModeAsync, chatSend]);

  const cancelTake = useCallback(() => {
    pending.current?.resolve(false);
    pending.current = null;
    setTakeAsked(false);
  }, []);

  const takeOver = useCallback(() => {
    if (canWrite) void setManualModeAsync(true);
  }, [canWrite, setManualModeAsync]);

  const confirmReturn = useCallback(() => {
    setReturnAsked(false);
    returnToBot.mutate();
  }, [returnToBot]);

  return {
    canWrite,
    manualMode,
    /** "Вернуть боту" имеет смысл: ручной режим или клиент передан менеджеру. */
    canReturn: manualMode || client.bot_silence?.reason === 'handed_to_human',
    busy: chat.isToggling || returnToBot.isPending,
    isSending: chat.isSending,
    send,
    takeOver,
    takeAsked,
    confirmTake,
    cancelTake,
    returnAsked,
    askReturn: () => setReturnAsked(true),
    cancelReturn: () => setReturnAsked(false),
    confirmReturn,
    accountOptions,
    sendAccountId: sendOption?.accountId ?? null,
    /** KAN-931: key of the chosen option (select value; chooseAccount takes it). */
    sendOptionKey: sendOption?.key ?? null,
    chooseAccount,
    sendFromText: senderAccountText(account),
  };
}

export type MobileManualChat = ReturnType<typeof useMobileManualChat>;
